import { useEffect, useState } from 'react';
import { GroupContractDto } from 'api/contract';
import { FileTypeEnum, NaturalPersonDto, LegalEntityDto, DocumentVerificationStatusEnum } from 'api/actor';
import { contractsAPI } from 'services/swagger-api';
import { CardsSection } from 'components/layout/Section/CardsSection';
import { GenericLoading } from 'components/ui/Loader';
import { GenericDocumentUploader } from 'components/domain/Document/GenericDocumentUploader';
import { IdentityDocumentUploader } from 'components/domain/IdentityUpload';
import PersonOverviewById from 'features/person-view/components/PersonOverviewById';
import { useFetchPersonWithDocuments } from 'features/new-actor/hooks/useFetchPersonWithDocuments';
import { useDynamicTranslation } from 'hooks/useDynamicTranslation';
import { NsKey } from 'enums/NsKey';
import { useSelector } from 'react-redux';
import { selectReferentialDataByKey } from 'features/referentials/selectors/selectReferentialDataByKey';
import { ReferentialKey } from 'api/referential';
import { useMemo } from 'react';

interface SubscriberLockedSummaryProps {
  groupContractId: string;
}

/**
 * Résumé read-only du souscripteur pour un contrat verrouillé.
 * Réutilise `PersonOverviewById` (rendu selon physique/morale) + une section documents
 * avec les uploaders en mode désactivé (affichage seul, pas d'upload/remove).
 */
export const SubscriberLockedSummary = ({ groupContractId }: SubscriberLockedSummaryProps) => {
  const [contract, setContract] = useState<GroupContractDto | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await contractsAPI.getGroupContractByID({ groupContractId });
        if (!cancelled) setContract(data);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [groupContractId]);

  const subscriberId = (contract?.subscriber as { id?: string } | undefined)?.id ?? null;

  if (loading) return <GenericLoading loading isParentSized />;
  if (!subscriberId) return null;

  return (
    <div className="space-y-6">
      <PersonOverviewById personId={subscriberId} />
      <SubscriberDocumentsReadOnly personId={subscriberId} />
    </div>
  );
};

interface SubscriberDocumentsReadOnlyProps {
  personId: string;
}

/**
 * Documents du souscripteur en mode read-only.
 * - Physique : CNI + RIB
 * - Morale  : KBIS + CNI de chaque gérant
 */
const SubscriberDocumentsReadOnly = ({ personId }: SubscriberDocumentsReadOnlyProps) => {
  const { t } = useDynamicTranslation([NsKey.Common]);
  const { person, personType, documents, memberDocuments, isLoading } =
    useFetchPersonWithDocuments(personId);

  const companyRoleReferentials = useSelector(
    selectReferentialDataByKey(ReferentialKey.CompanyRole)
  );
  const gerantRoleId = useMemo(
    () => companyRoleReferentials?.find(r => r.code === 'GERANT')?.id,
    [companyRoleReferentials]
  );

  const gerants = useMemo((): NaturalPersonDto[] => {
    if (personType !== 'morale' || !gerantRoleId || !person) return [];
    return (person as LegalEntityDto).naturalPersons?.filter(np => np.companyRole === gerantRoleId) ?? [];
  }, [person, personType, gerantRoleId]);

  if (isLoading) return null;

  const formatName = (firstName?: string | null, lastName?: string | null) => {
    const parts = [firstName, lastName].filter((p): p is string => !!p && p.trim().length > 0);
    return parts.length > 0 ? parts.join(' ') : undefined;
  };

  return (
    <CardsSection title={t('COMMON.LABELS.DOCUMENTS')}>
      <div className="flex flex-col gap-3">
        {personType === 'physique' && person && (
          <>
            <IdentityDocumentUploader
              documentName={t('DOCUMENT_TYPE.ID_CARD')}
              naturalPersonId={personId}
              personDisplayName={formatName(
                (person as NaturalPersonDto).firstName,
                (person as NaturalPersonDto).lastName,
              )}
              storedFileId={documents.cni?.storedFileId}
              verificationStatus={documents.cni?.verificationStatus}
              onSuccess={async () => { /* read-only */ }}
              onRemove={async () => { /* read-only */ }}
              disabled
            />
            <GenericDocumentUploader
              documentName={t('DOCUMENT_TYPE.RIB')}
              fileType={FileTypeEnum.Rib as unknown as never}
              storedFileId={documents.rib?.storedFileId}
              onUpload={async () => ({ success: true })}
              onRemove={async () => { /* read-only */ }}
              disabled
            />
          </>
        )}

        {personType === 'morale' && (
          <>
            <GenericDocumentUploader
              documentName={t('DOCUMENT_TYPE.KBIS')}
              fileType={FileTypeEnum.Kbis as unknown as never}
              storedFileId={documents.kbis?.storedFileId}
              onUpload={async () => ({ success: true })}
              onRemove={async () => { /* read-only */ }}
              disabled
            />
            {gerants.map(gerant => {
              if (!gerant.id) return null;
              const gerantCni = (memberDocuments[gerant.id] ?? []).find(
                d => d.fileType === FileTypeEnum.PieceIdentite,
              );
              const displayName = formatName(gerant.firstName, gerant.lastName);
              const label = displayName
                ? `${t('DOCUMENT_TYPE.ID_CARD')} - ${displayName}`
                : t('DOCUMENT_TYPE.ID_CARD');
              return (
                <IdentityDocumentUploader
                  key={gerant.id}
                  documentName={label}
                  naturalPersonId={gerant.id}
                  personDisplayName={displayName}
                  storedFileId={gerantCni?.storedFileId}
                  verificationStatus={gerantCni?.verificationStatus as DocumentVerificationStatusEnum | undefined}
                  onSuccess={async () => { /* read-only */ }}
                  onRemove={async () => { /* read-only */ }}
                  disabled
                />
              );
            })}
          </>
        )}
      </div>
    </CardsSection>
  );
};
